import { FC, useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Box,
  Divider,
  Button,
  Snackbar,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useGuardianState } from '../../contexts/guardian.context';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { Save } from '@mui/icons-material';

const GUARDIAN_SETTINGS_KEY = 'guardian_settings';

const GuardianSettingsView: FC = () => {
  const { translateText } = useTranslation();
  const { selectedChildId } = useGuardianState();
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const loadSettings = () => {
    try {
      const saved = localStorage.getItem(GUARDIAN_SETTINGS_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    }
    return {
      notifications: {
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true,
        feeReminders: true,
        eventReminders: true,
        academicUpdates: true,
      },
      preferences: {
        language: 'en',
        theme: 'light',
      },
    };
  };

  const [notifications, setNotifications] = useState(loadSettings().notifications);
  const [preferences, setPreferences] = useState(loadSettings().preferences);

  useEffect(() => {
    const saved = loadSettings();
    setNotifications(saved.notifications);
    setPreferences(saved.preferences);
  }, []);

  const handleNotificationChange = (key: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setNotifications((prev: typeof notifications) => ({
      ...prev,
      [key]: event.target.checked,
    }));
  };

  const handleSave = () => {
    try {
      const settings = { notifications, preferences };
      localStorage.setItem(GUARDIAN_SETTINGS_KEY, JSON.stringify(settings));
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveError(true);
      setTimeout(() => setSaveError(false), 3000);
    }
  };

  const handleLanguageChange = (event: any) => {
    setPreferences((prev: typeof preferences) => ({
      ...prev,
      language: event.target.value,
    }));
  };

  const handleThemeChange = (event: any) => {
    setPreferences((prev: typeof preferences) => ({
      ...prev,
      theme: event.target.value,
    }));
  };

  return (
    <Grid container spacing={3} sx={{ p: 3 }} data-testid="guardian-settings-container">
      <Grid item xs={12}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            {translateText('guardian.settings.title')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            data-testid="guardian-settings-save"
          >
            {translateText('common.save')}
          </Button>
        </Box>
      </Grid>

      {/* Notifications Settings */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {translateText('guardian.settings.notifications.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {translateText('guardian.settings.notifications.description')}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.emailNotifications}
                      onChange={handleNotificationChange('emailNotifications')}
                      data-testid="guardian-settings-email-notifications"
                    />
                  }
                  label={translateText('guardian.settings.notifications.emailNotifications')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.smsNotifications}
                      onChange={handleNotificationChange('smsNotifications')}
                      data-testid="guardian-settings-sms-notifications"
                    />
                  }
                  label={translateText('guardian.settings.notifications.smsNotifications')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.pushNotifications}
                      onChange={handleNotificationChange('pushNotifications')}
                      data-testid="guardian-settings-push-notifications"
                    />
                  }
                  label={translateText('guardian.settings.notifications.pushNotifications')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.feeReminders}
                      onChange={handleNotificationChange('feeReminders')}
                      data-testid="guardian-settings-fee-reminders"
                    />
                  }
                  label={translateText('guardian.settings.notifications.feeReminders')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.eventReminders}
                      onChange={handleNotificationChange('eventReminders')}
                      data-testid="guardian-settings-event-reminders"
                    />
                  }
                  label={translateText('guardian.settings.notifications.eventReminders')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifications.academicUpdates}
                      onChange={handleNotificationChange('academicUpdates')}
                      data-testid="guardian-settings-academic-updates"
                    />
                  }
                  label={translateText('guardian.settings.notifications.academicUpdates')}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Preferences */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {translateText('guardian.settings.preferences.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {translateText('guardian.settings.preferences.description')}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{translateText('guardian.settings.preferences.language')}</InputLabel>
                  <Select
                    value={preferences.language}
                    onChange={handleLanguageChange}
                    label={translateText('guardian.settings.preferences.language')}
                    data-testid="guardian-settings-language-select"
                  >
                    <MenuItem value="en">{translateText('guardian.settings.preferences.languages.english')}</MenuItem>
                    <MenuItem value="hi">{translateText('guardian.settings.preferences.languages.hindi')}</MenuItem>
                    <MenuItem value="ka">{translateText('guardian.settings.preferences.languages.kannada')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>{translateText('guardian.settings.preferences.theme')}</InputLabel>
                  <Select
                    value={preferences.theme}
                    onChange={handleThemeChange}
                    label={translateText('guardian.settings.preferences.theme')}
                    data-testid="guardian-settings-theme-select"
                  >
                    <MenuItem value="light">{translateText('guardian.settings.preferences.themes.light')}</MenuItem>
                    <MenuItem value="dark">{translateText('guardian.settings.preferences.themes.dark')}</MenuItem>
                    <MenuItem value="auto">{translateText('guardian.settings.preferences.themes.auto')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Account Info */}
      {selectedChildId && (
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {translateText('guardian.settings.accountInfo.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {translateText('guardian.settings.accountInfo.description')}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    {translateText('guardian.settings.accountInfo.selectedChild')}
                  </Typography>
                  <Typography variant="body1">
                    {selectedChildId}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      )}

      {/* Success/Error Snackbars */}
      <Snackbar
        open={saveSuccess}
        autoHideDuration={3000}
        onClose={() => setSaveSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>
          {translateText('guardian.settings.saveSuccess')}
        </Alert>
      </Snackbar>
      <Snackbar
        open={saveError}
        autoHideDuration={3000}
        onClose={() => setSaveError(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="error" onClose={() => setSaveError(false)}>
          {translateText('guardian.settings.saveError')}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default GuardianSettingsView;
